<template>
  <div class="drug-register-view view">
    <div class="actions-panel panel" style="width: fit-content; display: flex; flex-direction: column;">
      <h3 class="panel-header">Actions</h3>
      <button @click="toggleNewRequisitionModal()" class="actions-menu-item" v-tooltip.right="'Create a new Requisition request'">Create Requisition</button>
    </div>
    
    <div class="panel" v-if="$store.state.user.role.permissions.includes('can_view_transfers') && $store.state.user.requisitions_active">
      <h3 class="panel-header">Requisitions</h3>
      <div class="drug-register-table table-container">
        <div class="filters" style="display: flex; flex-wrap: wrap; align-items: center;">
          <!-- SEARCH SECTION -->
          <div style="display: flex; align-items: center; margin-right: 1em">
            <div class="text-search-container search-container">
             <input v-model="form.filters.location" @input="debouncedLoadEntries" id="location" placeholder="Search a Location..." style="position: relative" />
            </div>

            <div class="date-search-container search-container">
              <Calendar v-model="date" selectionMode="range" placeholder="Date Range..." dateFormat="dd/mm/yy" />
            </div>

            <button class="button submit-button" @click="loadRequisitions()">Search</button>
            <button class="button clear-button" @click="clearRequisitionsFilters()">Clear</button>
          </div>

          <!-- STATUS MULTISELECT -->
          <MultiSelect v-model="selectedStatuses" :options="statusSelect" @change="updateStatusList" optionLabel="status" placeholder="Status" />

          <!-- Column toggle rather than a filter, so it changes what is shown without reloading. There is
               no batch toggle: a requisition requests stock another location holds, and the batch is
               decided when they dispense it. -->
          <div class="select-filters-container">
            <p @click="showClients = !showClients"
              v-bind:class="[showClients === true ? 'active-select-filter' : '']" class="select-filter-item">Show {{ $resident({ plural: true, capitalize: true }) }}</p>
          </div>
        </div>

        <div v-if="!$store.state.loadingRequisitionsData" style="display: flex; align-items: start; overflow-y: scroll">
          <div class="drug-register-table table-container" style="padding-top: 10em;" v-if='!$store.state.user.role.permissions.includes("can_view_transfers")'>
            <h3 style="border: 2px solid black; border-bottom: 2px solid black; width: fit-content; height: fit-content; margin: 0 auto; padding: 5px; border-radius: 4px">You do not have permission to access this feature. Please talk to your admin if you require access.</h3>
          </div>
          
          <table v-else class="data-table" cellspacing="0">
            <tr class="table-headers">
              <th style="width: 50px"></th>
              <th height=40px style="width: 70px">
                <h3 @click="sortBy('type', 'asc')">Type</h3>
              </th>
              <th>
                <h3>Location</h3>
              </th>
              <th>
                <h3>Requested By</h3>
              </th>
              <th>
                <h3 @click="sortBy('created_at', 'asc')" style="cursor: pointer">Requested Date</h3>
              </th>
              <th>
                <h3>Completed By</h3>
              </th>
              <th>
                <h3 @click="sortBy('completed_at', 'desc')">Completed Date</h3>
              </th>
              <th>
                <h3 @click="sortBy('status', 'desc')" style="padding-left: 20px;">Status</h3>
              </th>
            </tr>
            
            <tbody class="table-data" v-for="(item, index) in getDrugEntries" :key="index">
              <tr @click="showContent(item, index)" :class="[ item.display && (item.display == true) ? 'open-transfer-row': '']">
                <td>
                  <i v-if="item.display && (item.display == true)" class="pi pi-angle-down"></i>
                  <i v-else class="pi pi-angle-right"></i>
                </td>
                <td height="40px" style="width: 70px;">
                  <p v-if="item.to && item.to.uuid == $store.state.currentLocation.uuid" class="blue-button" style="cursor: default; width: 45px; padding: 2px; text-align: center; font-weight: bold">IN</p>
                  <p v-else class="blue-button" style="cursor: default; width: 45px; padding: 2px; text-align: center; font-weight: bold; background-color: #9F5CC0 !important">OUT</p>
                </td>
                <td>{{ item.to && item.to.uuid == $store.state.currentLocation.uuid ? (item.from ? item.from.name : 'Unknown') : (item.to ? item.to.name : 'Unknown') }}</td>
                <td>{{ item.requested_by && item.requested_by.personal_information ? (item.requested_by.personal_information.firstname + ' ' + item.requested_by.personal_information.lastname) : '--' }}</td>
                <td>{{ item.requested_date || '--' }}</td>
                <td>{{ item.completed_by && item.completed_by.personal_information ? (item.completed_by.personal_information.firstname + ' ' + item.completed_by.personal_information.lastname) : '--' }}</td>
                <td>{{ item.completed_date || '--' }}</td>
                <td v-if="item.status">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <p :class="[item.status == 'complete' ? 'correct-status-color' : (item.status == 'cancelled' ? 'mismatch-status-color' : 'warning-status-color')]">
                      {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
                    </p>
                    <!-- Marking ready is the supplying location's action: `from` is the pharmacy.
                         This had no location check at all, so a ward could mark its own request
                         ready for pickup.

                         It was also missing the distribution_date check the expanded panel's
                         equivalent button has (below), so it showed for the entire 'pending'
                         status window rather than only after supply - i.e. it and "Supply
                         Requisition" were visible together, when the lifecycle is supply, then
                         ready-for-pickup, never both at once. -->
                    <button
                      v-if="item.status == 'pending' && item.from && item.from.uuid == $store.state.currentLocation.uuid && item.distribution_date"
                      @click.stop="requisitionAction('Ready', item)"
                      class="transfer-action-button complete-button"
                      style="margin: 0; padding: 4px 10px; font-size: 0.85em; height: 28px; line-height: 1;"
                    >
                      Ready for Pickup
                    </button>
                  </div>
                </td>
              </tr>
              
              <!-- Expansion is bound, not poked into the DOM. Supplying a requisition refreshes the
                   list from the store, which replaces every row object - so `display` and
                   `expandedInfo` reset together and the row closes. An inline style set by hand
                   survived that refresh, leaving the row open on a spinner with nothing to load. -->
              <tr :ref="'expand-'+index" :id="'expand-'+index" class="expanded-content" :style="item.display ? { display: 'table-row' } : {}">
                <div class="expanded-transfer-container">
                  <div v-if="!item.expandedInfo && !item.expandError" style="margin: 0 auto; margin-bottom: 1em;">
                    <i class="pi pi-spin pi-spinner" style="font-size: 3rem !important; margin: 1.5em 0;"></i>
                  </div>
                  <div v-if="item.expandError" style="margin: 1.5em auto; text-align: center;">
                    <p class="mismatch-status-color" style="display: inline-block; padding: 8px 12px;">{{ item.expandError }}</p>
                  </div>
                  
                  <div class="transfer-specs-container" v-if="item.expandedInfo">
                    <div class="status-field">
                      <p class="field-title">Status</p>
                      <p class="field-value" style="text-align: center" :class="[item.status == 'complete' ? 'correct-status-color' : (item.status == 'cancelled' ? 'mismatch-status-color' : 'warning-status-color')]">
                        {{ item.status.charAt(0).toUpperCase() + item.status.slice(1) }}
                      </p>
                    </div>
                    <div class="type-field">
                      <p class="field-title">Type</p>
                      <p class="field-value" style="text-align: center" :class="[item.to && item.to.uuid == $store.state.currentLocation.uuid ? 'received-status-color' : 'sent-status-color']">
                        {{ item.to && item.to.uuid == $store.state.currentLocation.uuid ? 'Inbound Requisition' : 'Outbound Requisition' }}
                      </p>
                    </div>
                    <div class="date-received-field">
                      <p class="field-title">Requested Date</p>
                      <p class="field-value">{{ item.expandedInfo.requested_date || 'N/A' }}</p>
                    </div>
                    <div class="fulfilled-time-field" v-if="item.expandedInfo.distribution_date">
                      <p class="field-title">Supplied Date</p>
                      <p class="field-value">{{ item.expandedInfo.distribution_date }}</p>
                    </div>
                    <div class="pending-time-field" v-if="item.expandedInfo.pickup_date">
                      <p class="field-title">Pickup Date</p>
                      <p class="field-value">{{ item.expandedInfo.pickup_date }}</p>
                    </div>
                    <div class="collection-time-field" v-if="item.expandedInfo.completed_date">
                      <p class="field-title">Completed Date</p>
                      <p class="field-value">{{ item.expandedInfo.completed_date }}</p>
                    </div>
                    <!-- Stock flows from -> to, so `from` is the supplying location and `to` is
                         the requesting location. These labels were the wrong way round. -->
                    <div class="ordered-from-field">
                      <p class="field-title">From Location (Supplying)</p>
                      <p class="field-value">{{ item.expandedInfo.from ? item.expandedInfo.from.name : 'N/A' }}</p>
                    </div>
                    <div class="ordered-by-field">
                      <p class="field-title">To Location (Requesting)</p>
                      <p class="field-value">{{ item.expandedInfo.to ? item.expandedInfo.to.name : 'N/A' }}</p>
                    </div>
                    <div class="ordered-witness-field">
                      <p class="field-title">Requested By</p>
                      <p class="field-value">{{ item.expandedInfo.requested_by && item.expandedInfo.requested_by.personal_information ? (item.expandedInfo.requested_by.personal_information.firstname + ' ' + item.expandedInfo.requested_by.personal_information.lastname) : 'N/A' }}</p>
                    </div>
                    <div class="fulfilled-by-field" v-if="item.expandedInfo.distributed_by">
                      <p class="field-title">Supplied By</p>
                      <p class="field-value">{{ item.expandedInfo.distributed_by.personal_information ? (item.expandedInfo.distributed_by.personal_information.firstname + ' ' + item.expandedInfo.distributed_by.personal_information.lastname) : 'N/A' }}</p>
                    </div>
                    <div class="pending-by-field" v-if="item.expandedInfo.picked_up_by">
                      <p class="field-title">Picked Up By</p>
                      <p class="field-value">{{ item.expandedInfo.picked_up_by.personal_information ? (item.expandedInfo.picked_up_by.personal_information.firstname + ' ' + item.expandedInfo.picked_up_by.personal_information.lastname) : 'N/A' }}</p>
                    </div>
                    <div class="collected-by-field" v-if="item.expandedInfo.completed_by">
                      <p class="field-title">Completed By</p>
                      <p class="field-value">{{ item.expandedInfo.completed_by.personal_information ? (item.expandedInfo.completed_by.personal_information.firstname + ' ' + item.expandedInfo.completed_by.personal_information.lastname) : 'N/A' }}</p>
                    </div>
                  </div>
                  
                  <div class="transfer-notes-container" v-if="item.expandedInfo">
                    <p class="field-title">Notes</p>
                    <div class="field-notes">
                      <p>{{ item.expandedInfo.notes || 'None' }}</p>
                    </div>
                  </div>
                  
                  <div class="transfer-drugs-container" v-if="item.expandedInfo">
                    <p class="field-title">Requested Medications</p>
                    <div class="drug-field-tables" style="width: 95%; border: 2px solid rgb(182, 182, 182); border-radius: 4px; height: 300px;">
                      <table style="padding: 0px 5px">
                        <tr>
                          <th>Name</th>
                          <th>Qty</th>
                          <th v-if="showClients">{{ $resident({ capitalize: true }) }}</th>
                          <th>Status</th>
                        </tr>
                      </table>
                      <div style="height: 250px; overflow-y: scroll; background-color: white;">
                        <table>
                          <tr v-for="(drug, key) in item.expandedInfo.requisition_items" :key="key" style="height: 25px">
                            <td style="font-size: 0.8em;">{{ drug.alias ? drug.alias.name : 'Unknown' }} ({{ drug.alias ? drug.alias.form : '' }})</td>
                            <td style="font-size: 0.8em;">{{ drug.quantity }}</td>
                            <td style="font-size: 0.8em;" v-if="showClients">{{ drug.patient && drug.patient.personal_information ? (drug.patient.personal_information.firstname + ' ' + drug.patient.personal_information.lastname) : 'Imprest/Emergency' }}</td>
                            <td style="font-size: 0.8em;">{{ drug.dispensings && drug.dispensings.length ? 'Dispensed' : 'Pending Dispense' }}</td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>
                  
                  <!-- REQUISITION ACTIONS -->
                  <div class="transfer-actions-container" v-if="item.expandedInfo">
                    <!-- CANCEL ACTION -->
                    <button 
                      @click="requisitionAction('Cancel', item)" 
                      class="transfer-action-button error-button"
                      v-show="item.status !== 'complete' && item.status !== 'cancelled'">
                      Cancel Requisition
                    </button>
                    
                    <!-- These conditions were written against the reversed direction. Stock flows
                         from -> to, so `from` is the supplying location (pharmacy) and `to` is the
                         requesting location (ward).

                         The two register movements sit at the ends of the lifecycle: dispensing
                         takes the stock out of the supplying register, and completion signs it into
                         the requesting one. The steps in between are about chain of custody rather
                         than register balances. -->

                    <!-- SUPPLY ACTION (Supplying Location Only)
                         Dispensing is where the stock leaves the supplying register. -->
                    <button
                      @click="openSupplyModal(item)"
                      class="transfer-action-button complete-button"
                      v-show="item.status == 'pending' && item.from && item.from.uuid == $store.state.currentLocation.uuid">
                      Supply Requisition
                    </button>

                    <!-- READY FOR PICKUP ACTION (Supplying Location Only, once supplied) -->
                    <button
                      @click="requisitionAction('Ready', item)"
                      class="transfer-action-button complete-button"
                      v-show="item.status == 'pending' && item.from && item.from.uuid == $store.state.currentLocation.uuid && item.expandedInfo.distribution_date">
                      Mark Ready for Pickup
                    </button>

                    <!-- PICKUP ACTION (Requesting Location Only)
                         Pickup does not touch the supplying register - the stock already left it at
                         dispensing. It records who collected the drugs, so it belongs to the
                         requesting location that is retrieving them. -->
                    <button
                      @click="requisitionAction('Pickup', item)"
                      class="transfer-action-button complete-button"
                      v-show="item.status == 'pickup ready' && item.to && item.to.uuid == $store.state.currentLocation.uuid">
                      Pickup Requisition
                    </button>

                    <!-- COMPLETE ACTION (Requesting Location Only)
                         The final sign-off: this is what lands the stock in the requesting register. -->
                    <button
                      @click="requisitionAction('Complete', item)"
                      class="transfer-action-button complete-button"
                      v-show="item.status == 'transit' && item.to && item.to.uuid == $store.state.currentLocation.uuid">
                      Complete Requisition
                    </button>

                    <button @click="showContent(item, index)" style="background-color: white; padding: 0;"><i class="pi pi-times"></i></button>
                  </div>
                </div>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <h3 v-if="$store.state.requisitionError">{{ $store.state.requisitionError }}</h3>
          <i v-else class="pi pi-spin pi-spinner" style="font-size: 3rem !important; margin-top: 4em;"></i>
        </div>
        
        <div class="pagination-links">
          <p class="pagination-arrow" v-show="$store.state.drugEntries.current_page > 1" @click="displayPage(form.page - 1)"> &larr; </p>
          <p>Page {{ $store.state.drugEntries.current_page }} of {{ $store.state.drugEntries.page_count }}</p>
          <p class="pagination-arrow" v-show="$store.state.drugEntries.current_page < $store.state.drugEntries.page_count" @click="displayPage($store.state.drugEntries.current_page + 1)"> &rarr; </p>
        </div>
      </div>
    </div>
    <div v-else class="drug-register-table table-container" style="padding-top: 5em; height: 78vh; grid-template-rows: [row1-start] 70px [row1-end] minmax(200px, 64vh) [third-line] 50px [fourth-line];">
      <h3 style="border: 2px solid black; border-bottom: 2px solid black; width: fit-content; height: fit-content; margin: 0 auto; padding: 5px; border-radius: 4px">You do not have permission to access this feature. Please talk to your admin if you require access.</h3>
    </div>
    
    <ActionModal v-if="displayActionModal" @close="close" :toAction="toAction" @isSuccessful="isSuccessful" :endpoint="getEndpoint()" />
    <NewRequisitionModal v-if="displayNewRequisitionModal" @close="close" :mode="requisitionMode" :requisitionData="selectedRequisition" />
  </div>
</template>

<script>
import ActionModal from '@/components/modals/ActionModal.vue';
import NewRequisitionModal from '@/components/modals/NewRequisitionModal.vue';
import axios from 'axios';
import debounce from "debounce";

export default {
  components: {
    ActionModal,
    NewRequisitionModal,
  },
  data() {
    return {
      // Column toggle. Default matches what each company type saw before the column became independent.
      showClients: !this.$store.getters.isWarehouseType,
      displayNewRequisitionModal: false,
      displayActionModal: false,
      requisitionMode: 'Create',
      selectedRequisition: null,
      toAction: {
        uuid: '',
        action: '',
      },
      selectedStatuses: [
        { status: 'Pending' },
        { status: 'Pickup Ready' },
        { status: 'Transit' },
        { status: 'Complete' }
      ],
      statusSelect: [
        { status: 'Pending' },
        { status: 'Pickup Ready' },
        { status: 'Transit' },
        { status: 'Complete' },
        { status: 'Cancelled' }
      ],
      date: [],
      form: {
        sort_direction: 'desc',
        col: 'created_at',
        page: 1,
        filters: {
          status: [],
          location: '',
          date: {
            start: '',
            end: ''
          }
        }
      }
    }
  },
  methods: {
    close(value) {
      if (value) {
        this.displayActionModal = false;
        this.displayNewRequisitionModal = false;
      }
    },
    isSuccessful(value) {
      if (value) {
        this.loadRequisitions();
      }
    },
    toggleNewRequisitionModal() {
      this.requisitionMode = 'Create';
      this.selectedRequisition = null;
      this.displayNewRequisitionModal = true;
    },
    openSupplyModal(item) {
      this.requisitionMode = 'Supply';
      this.selectedRequisition = item.expandedInfo;
      this.displayNewRequisitionModal = true;
    },
    requisitionAction(action, item) {
      this.displayActionModal = true;
      this.toAction = {
        action: action,
        uuid: item.uuid,
        note: (item.expandedInfo && item.expandedInfo.notes) ? item.expandedInfo.notes : ''
      };
    },
    getEndpoint() {
      if (this.toAction.action == 'Cancel') {
        return `/requisition/${this.toAction.uuid}/cancel`;
      } else if (this.toAction.action == 'Ready') {
        return "/mark-ready-requisition";
      } else if (this.toAction.action == 'Pickup') {
        return "/pickup-requisition";
      } else if (this.toAction.action == 'Complete') {
        return "/complete-requisition";
      }
    },
    sortBy(col, dir) {
      this.form.col = col;
      this.form.sort_direction = dir;
      this.loadRequisitions();
    },
    showContent(requisition, index) {
      if (requisition.display && requisition.display === true) {
        requisition.display = false;
      } else {
        requisition.display = true;
        requisition.expandError = '';
        axios.post('/get-requisition', { uuid: requisition.uuid }, this.$store.state.header).then(res => {
          requisition.expandedInfo = res.data;
        }).catch(err => {
          // Without this the panel sat on its loading spinner forever on any failure - a 403 from
          // the permission middleware, an expired token, a 404 - with nothing shown to the user
          // and nothing to diagnose from. The expanded panel renders on `expandedInfo`, so a
          // rejected request left it permanently blank.
          const data = err.response?.data;
          requisition.expandError = (typeof data === 'string' ? data : data?.message) || 'Could not load this requisition.';
          console.log('get-requisition failed', data || err);
        });
      }
    },
    loadRequisitions() {
      if (this.date.length) {
        if (typeof this.date == "string") {
          this.form.filters.date.start = this.date.split("-")[0];
          this.form.filters.date.end = this.date.split("-")[1];
        } else {
          this.form.filters.date.start = new Date(this.date[0]).toLocaleDateString();
          this.form.filters.date.end = this.date[1] ? new Date(this.date[1]).toLocaleDateString() : new Date(this.date[0]).toLocaleDateString();
        }
      } else {
        this.form.filters.date.start = '';
        this.form.filters.date.end = '';
      }
      this.$store.dispatch('getRequisitions', this.form);
    },
    debouncedLoadEntries: debounce(function() {
      this.loadRequisitions();
    }, 500),
    displayPage(page) {
      this.form.page = page;
      this.loadRequisitions();
    },
    updateStatusList() {
      this.form.filters.status = [];
      this.selectedStatuses.forEach(statusObject => {
        this.form.filters.status.push(statusObject.status.toLowerCase());
      });
      this.loadRequisitions();
    },
    clearRequisitionsFilters() {
      this.form = {
        sort_direction: 'desc',
        col: 'created_at',
        page: 1,
        filters: {
          status: ['pending', 'pickup ready', 'transit', 'complete'],
          location: '',
          date: {
            start: '',
            end: ''
          }
        }
      };
      this.selectedStatuses = [
        { status: 'Pending' },
        { status: 'Pickup Ready' },
        { status: 'Transit' },
        { status: 'Complete' }
      ];
      this.date = [];
      this.loadRequisitions();
    }
  },
  mounted() {
    this.updateStatusList();
  },
  computed: {
    getDrugEntries() {
      return this.$store.getters.getDrugEntries;
    }
  },
  watch: {
    date: {
      handler(newValue) {
        if (newValue?.length > 0) {
          this.debouncedLoadEntries();
        }
      },
      deep: false
    }
  }
}
</script>

<style lang="scss">
@import "../../assets/css/main.scss";
</style>
